import React, { useEffect, useMemo, useRef, useState } from 'react';
import InfoBar from '../components/InfoBar';
import StatCard from '../components/StatCard';
import SidebetWheel from '../components/SidebetWheel';
import { loadExternalScript } from '../utils/scriptLoader';
import { PageRevealStyles, usePageReveal } from '../components/PageReveal';
import SideBetsTotalsChart from '../components/charts/SideBetsTotalsChart';
import SideBetsPlayerCards from '../components/SideBetsPlayerCards';
import { SIDEBETS_CSV_BY_YEAR, getCsvUrls } from '../utils/dataSources';

const D3_URL = 'https://d3js.org/d3.v7.min.js';
const PLOT_URL = 'https://cdn.jsdelivr.net/npm/@observablehq/plot@0.6/dist/plot.umd.min.js';
const SIDEBETS_CSVS = getCsvUrls(SIDEBETS_CSV_BY_YEAR);

const PRIORITY_ORDER = ['VM', 'EJ', 'WBG', 'W2J'];
const COLOR_MAP = {
  VM: '#96e6ab',
  EJ: '#76ace0',
  WBG: '#df87a8',
  W2J: '#f4d353',
};
const FALLBACK_COLORS = ['#b3e2cd', '#fdcdac', '#cbd5e8', '#f4cae4', '#e6f5c9', '#fff2ae'];
const IMAGE_MAP = {
  VM: '/images/VM.png',
  EJ: '/images/EJ.png',
  WBG: '/images/WBG.png',
  W2J: '/images/W2J.png',
};
const FALLBACK_IMAGE = '/images/person.png';

const cleanCurrency = (value) => {
  if (value == null) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return +trimmed.replace(/\(/g, '-').replace(/\)/g, '').replace(/[$,]/g, '');
  }
  return null;
};

const formatSideBetCurrency = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  const absolute = Math.abs(value);
  const formatted = absolute.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
};

const toneForAmount = (value) => {
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
};

const mergeSideBetTotals = (rows) =>
  rows.reduce((acc, row) => {
    Object.entries(row).forEach(([name, rawValue]) => {
      if (!name) return;
      const amount = cleanCurrency(rawValue);
      if (amount == null || Number.isNaN(amount)) return;
      acc[name] = (acc[name] ?? 0) + amount;
    });
    return acc;
  }, {});

const getSidebetsUrls = (selectedYear) => {
  if (selectedYear == null) return SIDEBETS_CSVS;
  const url = SIDEBETS_CSV_BY_YEAR[selectedYear];
  return url ? [url] : [];
};

const SideBetsPage = ({ isActive, selectedYear }) => {
  const rootRef = useRef(null);
  usePageReveal(
    rootRef,
    isActive,
    '.stats-container, .stats-container-three, .chart-container, .extra-stats-container, .player-cards-container, .player-cards-container .player-card, .sidebet-wheel-card',
  );

  const [d3Lib, setD3Lib] = useState(null);
  const [plotLib, setPlotLib] = useState(null);
  const [sortedPlayers, setSortedPlayers] = useState([]);
  const [visiblePlayers, setVisiblePlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isActive) return undefined;
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      try {
        const d3 = await loadExternalScript(D3_URL, 'd3');
        const Plot = await loadExternalScript(PLOT_URL, 'Plot');
        if (cancelled) return;

        setD3Lib(d3);
        setPlotLib(Plot);

        const urls = getSidebetsUrls(selectedYear);
        if (!urls.length) {
          setSortedPlayers([]);
          setVisiblePlayers([]);
          setError(new Error('No side bet data available for the selected year.'));
          return;
        }

        const totalsSets = await Promise.all(urls.map((url) => d3.csv(url, d3.autoType)));
        if (cancelled) return;

        const totalsRows = totalsSets.map((rows) => rows?.[0]).filter(Boolean);
        if (!totalsRows.length) {
          setSortedPlayers([]);
          setVisiblePlayers([]);
          setError(new Error('No side bet data available.'));
          return;
        }

        const totalsRow =
          selectedYear == null && totalsRows.length > 1 ? mergeSideBetTotals(totalsRows) : totalsRows[0];
        const playersData = Object.entries(totalsRow)
          .map(([name, rawValue]) => {
            if (!name) return null;
            const amount = cleanCurrency(rawValue);
            if (amount == null || Number.isNaN(amount)) return null;
            return { name, total: amount };
          })
          .filter(Boolean);

        const mainPlayers = playersData
          .filter((entry) => PRIORITY_ORDER.includes(entry.name))
          .sort((a, b) => PRIORITY_ORDER.indexOf(a.name) - PRIORITY_ORDER.indexOf(b.name));
        const otherPlayers = playersData
          .filter((entry) => !PRIORITY_ORDER.includes(entry.name))
          .sort((a, b) => a.name.localeCompare(b.name));

        const sorted = [...mainPlayers, ...otherPlayers];

        if (!cancelled) {
          setSortedPlayers(sorted);
          setVisiblePlayers(mainPlayers);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [isActive, selectedYear]);

  const summary = useMemo(() => {
    if (!sortedPlayers.length) {
      return {
        leader: null,
        trailer: null,
        totalPlayers: 0,
        winningPlayers: 0,
        losingPlayers: 0,
        evenPlayers: 0,
        totalAction: 0,
      };
    }

    const leader = sortedPlayers.reduce(
      (max, player) => (player.total > max.total ? player : max),
      sortedPlayers[0],
    );
    const trailer = sortedPlayers.reduce(
      (min, player) => (player.total < min.total ? player : min),
      sortedPlayers[0],
    );
    const winningPlayers = sortedPlayers.filter((player) => player.total > 0).length;
    const losingPlayers = sortedPlayers.filter((player) => player.total < 0).length;
    const evenPlayers = sortedPlayers.length - winningPlayers - losingPlayers;
    const totalAction = sortedPlayers.reduce(
      (sum, player) => sum + Math.abs(player.total),
      0,
    );

    return {
      leader,
      trailer,
      totalPlayers: sortedPlayers.length,
      winningPlayers,
      losingPlayers,
      evenPlayers,
      totalAction,
    };
  }, [sortedPlayers]);

  if (!isActive) return null;

  return (
    <section className="app-page" ref={rootRef}>
      <PageRevealStyles />

     

      <div className="stats-container-three">
        <div className="stats-card-image">
          <img src="/images/EJ.png" alt="EJ" />
        </div>
        <div className="stats-card-text">
          <p>
            It's all about the side bets. <b>RUN IT BACK</b> one more time! You can use our sidebet wheel below!
          </p>
        </div>
      </div>
 <SidebetWheel />
     <InfoBar type="info" title="Community compiled stats"> 
        <p>
          Thanks to the amazing people on Discord who compile these stats and share them with the community. The stats below
          are pulled directly from the side-bet tracking the Discord team maintains. Huge thanks to Train and the entire stats
          crew for their work! Join the{' '}
          <a href="https://discord.gg/vegasmatt" target="_blank" rel="noopener noreferrer">
            Vegas Matt Discord
          </a>{' '}
          to get involved.
        </p>
      </InfoBar>

      <div className="stats-container">
        <StatCard
          title="Current Leader"
          value={
            loading
              ? 'Loading…'
              : summary.leader
              ? `${summary.leader.name} (${formatSideBetCurrency(summary.leader.total)})`
              : '—'
          }
          tone={summary.leader ? toneForAmount(summary.leader.total) : 'neutral'}
        />
        <StatCard
          title="Deepest Hole"
          value={
            loading
              ? 'Loading…'
              : summary.trailer
              ? `${summary.trailer.name} (${formatSideBetCurrency(summary.trailer.total)})`
              : '—'
          }
          tone={summary.trailer ? toneForAmount(summary.trailer.total) : 'neutral'}
        />
        <StatCard
          title="Players Tracked"
          value={loading ? 'Loading…' : summary.totalPlayers || '—'}
         
        />
        <StatCard
          title="Total Action"
          value={loading ? 'Loading…' : formatSideBetCurrency(summary.totalAction)}
        />
      </div>

      <div className="chart-container" id="sideBetsTotals">
        <h2>SIDE BET TOTALS</h2>
        <SideBetsTotalsChart
          d3={d3Lib}
          Plot={plotLib}
          players={visiblePlayers}
          colorMap={COLOR_MAP}
          fallbackColors={FALLBACK_COLORS}
          imageMap={IMAGE_MAP}
          loading={loading}
          error={error}
          formatCurrency={formatSideBetCurrency}
        />
      </div>

      <SideBetsPlayerCards
        players={sortedPlayers}
        imageMap={IMAGE_MAP}
        fallbackImage={FALLBACK_IMAGE}
        loading={loading}
        error={error}
        formatCurrency={formatSideBetCurrency}
      />

     
    </section>
  );
};

export default SideBetsPage;
